import { Injectable, NgModule } from "@angular/core";
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  Resolve,
  RouterModule,
  Routes,
} from "@angular/router";
import { LoggedInGuard } from "../auth/auth.guards";
import { FlowComponent } from "./flow/flow.component";
import { FacilityService } from "../facilities/facility.service";
import { SessionService } from "../session.service";
import { take } from "rxjs/operators";
import { FlowsLibraryComponent } from "./flows-library/flows-library.component";
import { FlowsService } from "./flows.service";
import { IntegrationsService } from "../integrations/integrations.service";
import { ReportComponent } from "./report/report.component";
import {
  FlowsListGuard,
  FlowsWriteGuard,
  FlowsReportGuard,
} from "./flows.guards";
import { PermissionService } from "../permission.service";

@Injectable({ providedIn: "root" })
class FlowsWritePermissionResolve implements Resolve<any> {
  constructor(private PermissionService: PermissionService) {}
  resolve() {
    return Promise.all([
      this.PermissionService.categories.logicTrees.hasWrite(),
    ]).then(([hasFlowsWrite]) => {
      return {
        hasFlowsWrite,
      };
    });
  }
}

/*
@Injectable({ providedIn: "root" })
export class TypeResolve implements Resolve<any> {
  constructor(
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    return route.params.type;
  }
}
*/

@Injectable({ providedIn: "root" })
export class StartDateResolve implements Resolve<any> {
  constructor() {}

  resolve() {
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    start.setHours(0, 0, 0, 0);
    return start;
  }
}

@Injectable({ providedIn: "root" })
export class EndDateResolve implements Resolve<any> {
  constructor() {}

  resolve() {
    const end = new Date();
    end.setHours(23, 59, 59);
    return end;
  }
}

@Injectable({ providedIn: "root" })
export class FacilitiesResolve implements Resolve<any> {
  constructor(
    private FacilityService: FacilityService,
    private SessionService: SessionService,
  ) {}
  resolve() {
    return this.FacilityService.getFacilities(
      this.SessionService.currentAccountValue!.accountID,
      {
        page: 0,
        perPage: 999999999,
      },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class FlowsResolve implements Resolve<any> {
  constructor(
    private FlowsService: FlowsService,
    private SessionService: SessionService,
  ) {}
  resolve() {
    const facilitieIDs = this.SessionService.selectedFacilities?.map(
      (facility) => {
        return facility.id;
      },
    );

    return this.FlowsService.getLogicTrees(
      this.SessionService.currentAccountValue!.accountID,
      {
        facility_id: facilitieIDs,
        page: 0,
        perPage: 999999999,
      },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class FlowResolve implements Resolve<any> {
  constructor(
    private FlowsService: FlowsService,
    private SessionService: SessionService,
  ) {}
  resolve(route: ActivatedRouteSnapshot) {
    return this.FlowsService.getLogicTree(
      this.SessionService.currentAccountValue!.accountID,
      route.params.flowID,
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class ReportResolve implements Resolve<any> {
  constructor(
    private FlowsService: FlowsService,
    private SessionService: SessionService,
  ) {}
  resolve(route: ActivatedRouteSnapshot) {
    const start = route.parent!.data.start;
    const end = route.parent!.data.end;

    return this.FlowsService.getReport(
      this.SessionService.currentAccountValue!.accountID,
      route.params.flowID,
      {
        startDate: start.toISOString(),
        endDate: end.toISOString(),
      },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class FlowsIntegrationsResolve implements Resolve<any> {
  constructor(
    private IntegrationsService: IntegrationsService,
    private SessionService: SessionService,
  ) {}
  resolve(route: ActivatedRouteSnapshot) {
    return this.IntegrationsService.getIntegrations(
      route.params.type == 'app' ? this.SessionService.currentAccountValue!.accountID : undefined,
      route.params.type == 'app' ? this.SessionService.currentAppValue!.id : undefined,
      false
    ).pipe(take(1));
  }
}

const routes: Routes = [
  {
    path: "",
    canActivate: [LoggedInGuard],
    runGuardsAndResolvers: "always",
    data: { sidebar: "flows" },
    children: [
      {
        path: "library/:type",
        component: FlowsLibraryComponent,
        canActivate: [FlowsListGuard],
        data: { title: "Flow Library" },
        resolve: {
          /*type: TypeResolve,*/
          hasFlowsWrite: FlowsWritePermissionResolve
        },
      },
      {
        path: "create/:type",
        component: FlowComponent,
        resolve: { facilities: FacilitiesResolve, /*type: TypeResolve, */integrations: FlowsIntegrationsResolve },
        data: { title: "Create New Flow" },
        canActivate: [FlowsWriteGuard],
      },
      {
        path: "edit/:type/:flowID",
        component: FlowComponent,
        resolve: { facilities: FacilitiesResolve, flow: FlowResolve, /*type: TypeResolve, */integrations: FlowsIntegrationsResolve },
        canActivate: [FlowsWriteGuard],
        data: { title: "Edit Flow" },
      },
      {
        path: "report",
        resolve: {
          start: StartDateResolve,
          end: EndDateResolve,
        },
        children: [
          {
            path: ":flowID",
            component: ReportComponent,
            resolve: { report: ReportResolve },
            canActivate: [FlowsReportGuard],
            data: { title: "Flow Report" },
          },
        ],
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class FlowsRoutingModule {}

const flowsModuleLazyLoadRoutes: Routes = [
  {
    path: "flows",
    loadChildren: () => import("./flows.module").then((m) => m.FlowsModule),
  },
];

export { flowsModuleLazyLoadRoutes };
